import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { EMPTY, catchError, filter, map, switchMap, withLatestFrom } from 'rxjs'

import {
  ContextItem,
  ConversationOutcomeClassification,
  ConversationOutcomeType,
  ConversationThreadContextDataItemType,
  ConversationThreadExecutionPlanActionTargetExpressionType,
  ConversationThreadExecutionPlanActionTargetType,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  AiConversationModel,
  ContextDataKeys,
  MessageType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { AiConversationService } from '@flowx/designer/modules/ai/conversation/ai-conversation.service'
import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { AiConversationServiceActions } from '@flowx/designer/modules/ai/store/ai.actions'
import {
  AppNavigationService,
  NavigationActions,
} from '@flowx/designer/shared/app-navigation/app-navigation.service'
import { ProcessDataModelActions } from '@flowx/designer/shared/store/actions/process-data-model.actions'
import {
  selectCurrentResourceId,
  selectResourceDataModelEntities,
} from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { nodesSelector, selectedNode } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectAppId,
  selectAppVersionId,
  selectProcessResourceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import { AiDeveloperActions } from './developer.actions'

import { TypeAreaActions } from '../../../conversation/conversation.actions'
import { DataModelDraft } from '../../../types/ai.types'
import { parseCustomComponentOutcome } from '../services/developer-custom-component-outcome.utils'
import { DeveloperDataModelService } from '../services/developer-data-model.service'
import { computeDependencyChanges } from '../services/developer-dependency.utils'

const ACTION_MESSAGES: Partial<Record<AgentAction, string>> = {
  [AgentAction.BusinessRule]:
    'Business rule has been generated. You can tweak the results by telling me what to change.',
  [AgentAction.EditBusinessRule]:
    'Business rule has been updated. You can continue refining it by telling me what to change.',
  [AgentAction.Expression]:
    'Expression has been generated. You can adjust it by telling me what to change.',
  [AgentAction.ComputedValue]:
    'Computed value has been generated. You can modify it by telling me what to change.',
  [AgentAction.ImproveCode]: 'Code has been improved.',
  [AgentAction.DocumentCode]: 'Code documentation has been added.',
  [AgentAction.FixCode]: 'Code has been fixed. ',
  [AgentAction.ReusableFunction]:
    'Reusable function has been generated. You can tweak the results by telling me what to change.',
  [AgentAction.EditReusableFunction]:
    'Reusable function has been updated. You can continue refining it by telling me what to change.',
}

const DEFAULT_ACTION_MESSAGE =
  'Action completed. You can tweak the results by telling me what to change.'

@Injectable()
export class DeveloperEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #appNavigationService = inject(AppNavigationService)
  #aiConversationService = inject(AiConversationService)
  #developerDataModelService = inject(DeveloperDataModelService)

  // Keep (accept) the generated code → additively apply its data-model changes. The code
  // save itself stays in the editor components; this runs alongside it off the same keepOutcome
  // action. Discard never reaches here, so the draft is simply dropped.
  applyDataModelOnKeep = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.keepOutcome),
      withLatestFrom(this.#store.select(ConversationSelectors.developerDataModelChanges)),
      filter(([, draft]) => this.#hasApplicableDataModelItems(draft)),
      switchMap(([, draft]) => {
        const resourceId = this.#resolveDataModelResourceId()
        const resourceEntities = this.#store.selectSignal(selectResourceDataModelEntities)()

        return this.#developerDataModelService
          .applyDataModelDraft(resourceId, resourceEntities, draft as DataModelDraft)
          .pipe(
            map(() => ProcessDataModelActions.loadEntities({ resourceId })),
            catchError((err) => {
              console.error('[AiDeveloper] Failed to apply data model changes:', err)
              return EMPTY
            })
          )
      })
    )
  )

  discardOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.discardOutcome),
      map(() => AiDeveloperActions.discardOutcomeSuccess())
    )
  )

  generateBrFromNodeAction = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiDeveloperActions.generateBrFromNodeAction),
        switchMap(() => {
          const node = this.#store.selectSignal(selectedNode)()
          const processResourceId = this.#store.selectSignal(selectProcessResourceId)()
          const appId = this.#store.selectSignal(selectAppId)()
          const appVersionUuid = this.#store.selectSignal(selectAppVersionId)()

          return this.#appNavigationService.execute({
            id: NavigationActions.generateBusinessRule,
            data: {
              nodeId: node.id,
              businessRuleIndex: -1,
              actionId: -1,
              processId: processResourceId,
              appId,
              appVersionUuid,
              processVersionUuid: processResourceId,
            },
          })
        })
      ),
    { dispatch: false }
  )

  aiDeveloperPromptSubmit = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(TypeAreaActions.promptSubmit),
        withLatestFrom(
          this.#store.select(ConversationSelectors.currentMessageAgent),
          this.#store.select(ConversationSelectors.activeAgentTarget)
        ),
        filter(([, agent, target]) => agent === AgentName.AiDeveloper && target.targetType != null),
        switchMap(([, , target]) => {
          const nodes = this.#store.selectSignal(nodesSelector)()
          const nodeId = parseInt(target.businessRule?.nodeId || target.expression?.nodeId, 10)
          const node = nodes.find((node) => node.id === nodeId)
          if (!node) {
            return EMPTY
          }

          const appId = this.#store.selectSignal(selectAppId)()
          const appVersionUuid = this.#store.selectSignal(selectAppVersionId)()
          const selectedProcessId = this.#store.selectSignal(selectProcessResourceId)()

          let navigationId: string
          if (target.targetType === ConversationThreadExecutionPlanActionTargetType.BusinessRule) {
            navigationId = NavigationActions.showBrArea
          } else {
            navigationId =
              target.expression.expressionType ===
              ConversationThreadExecutionPlanActionTargetExpressionType.Hide
                ? NavigationActions.showHideExpressionArea
                : NavigationActions.showDisableExpressionArea
          }

          return this.#appNavigationService.execute({
            id: navigationId,
            data: {
              nodeId: node.id,
              processId: selectedProcessId,
              processVersionUuid: selectedProcessId,
              appId,
              appVersionUuid,
              navigationAreaId: node.navigationAreaId,
              swimlaneId: node.swimlaneDefinitionId,
              businessRuleIndex: target.businessRule?.index,
              actionId: target.businessRule?.actionId,
              showHideExpression:
                target.expression?.expressionType ===
                ConversationThreadExecutionPlanActionTargetExpressionType.Hide,
              showDisableExpression:
                target.expression?.expressionType ===
                ConversationThreadExecutionPlanActionTargetExpressionType.Disable,
              templateIds: target.expression?.elementId,
            },
          })
        })
      ),
    { dispatch: false }
  )

  reapplyCodeSelectionMerge = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.threadMessageFetchSuccess),
      filter(({ thread }) => {
        const agent = thread?.messages?.[0]?.executionPlan?.actions?.[0]?.agent
        return (
          agent?.agent === AgentName.AiDeveloper &&
          [AgentAction.ImproveCode, AgentAction.DocumentCode].includes(agent.action as AgentAction)
        )
      }),
      map(({ thread }) => {
        const mergedOutcome = AiConversationModel.mergeCodeSelectionIntoOutcome(thread.messages[0])
        return mergedOutcome
          ? ConversationActions.updateThreadOutcome({ threadId: thread.id, outcome: mergedOutcome })
          : null
      }),
      filter((action) => action !== null)
    )
  )

  generateBrStreamEnded = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiConversationServiceActions.conversationMessageStreamSuccess),
        withLatestFrom(
          this.#store.select(ConversationSelectors.activeAgentThread),
          this.#store.select(ConversationSelectors.errorOutcomeDetails)
        ),
        filter(([, , errorDetails]) => !errorDetails?.hasError),
        map(([data, activeThread]) => {
          const message = activeThread?.messages.find((message) => message.id === data.messageId)
          const agent = message?.executionPlan.actions?.[0]?.agent

          return {
            activeThread,
            message,
            agent,
          }
        }),
        filter(({ agent }) => agent?.agent === AgentName.AiDeveloper),
        filter(({ agent }) => {
          const action = agent.action as AgentAction
          return [
            AgentAction.BusinessRule,
            AgentAction.EditBusinessRule,
            AgentAction.Expression,
            AgentAction.ComputedValue,
            AgentAction.ImproveCode,
            AgentAction.DocumentCode,
            AgentAction.FixCode,
            AgentAction.ReusableFunction,
            AgentAction.EditReusableFunction,
          ].includes(action)
        }),
        switchMap(({ agent }) => {
          const action = agent.action as AgentAction
          const messageText = ACTION_MESSAGES[action] ?? DEFAULT_ACTION_MESSAGE

          return this.#aiConversationService.addStoreMessage({
            prompt: messageText,
            type: MessageType.SystemMsg,
          })
        })
      ),
    { dispatch: false }
  )

  customComponentStreamEnded = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiConversationServiceActions.conversationMessageStreamSuccess),
      withLatestFrom(
        this.#store.select(ConversationSelectors.activeAgentThread),
        this.#store.select(ConversationSelectors.errorOutcomeDetails)
      ),
      filter(([, , errorDetails]) => !errorDetails?.hasError),
      map(([data, activeThread]) => {
        const message = activeThread?.messages.find((m) => m.id === data.messageId)
        const agent = message?.executionPlan?.actions?.[0]?.agent
        return { message, agent }
      }),
      filter(
        ({ agent }) =>
          agent?.agent === AgentName.AiDeveloper && agent?.action === AgentAction.CustomComponent
      ),
      map(({ message }) => {
        const currentCodeRaw =
          message.contextData?.items?.find((item) => item.key === ContextDataKeys.Code)
            ?.valueGenericString ?? null
        const currentPackageJson = parseCustomComponentOutcome(currentCodeRaw)?.packageJson ?? null

        const mainOutcomeContent = message.outcome?.outcomes?.find(
          (o) =>
            o.outcomeType === ConversationOutcomeType.Textual &&
            o.outcomeClassification === ConversationOutcomeClassification.Main
        )?.textual?.content

        const outcome = parseCustomComponentOutcome(mainOutcomeContent)

        const changes = computeDependencyChanges(currentPackageJson, outcome?.packageJson ?? null)
        const hasChanges =
          changes.added.length > 0 || changes.updated.length > 0 || changes.removed.length > 0

        // Replace original code context with generated values so follow-up messages
        // carry the latest generated code as context
        const keysToReplace = new Set([
          ContextDataKeys.UIMessageType,
          ContextDataKeys.UIAiDeveloperDependencySummary,
          ContextDataKeys.Code,
        ] as string[])

        const contextDataItems = [
          ...(message.contextData?.items ?? []).filter((item) => !keysToReplace.has(item.key)),
          {
            key: ContextDataKeys.Code,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: JSON.stringify({
              jsx: outcome?.jsx ?? '',
              css: outcome?.css ?? '',
              packageJson: outcome?.packageJson ?? '',
            }),
          },
          {
            key: ContextDataKeys.UIMessageType,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: MessageType.SystemMsg,
          },
          ...(hasChanges
            ? [
                {
                  key: ContextDataKeys.UIAiDeveloperDependencySummary,
                  itemType: ConversationThreadContextDataItemType.GenericString,
                  valueGenericString: JSON.stringify(changes),
                },
              ]
            : []),
        ] as ContextItem[]

        return AiDeveloperActions.dependencySummaryReceived({
          messageText:
            'Component has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.',
          contextDataItems,
        })
      })
    )
  )

  customComponentSummaryMessage = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiDeveloperActions.dependencySummaryReceived),
        switchMap(({ messageText, contextDataItems }) =>
          this.#aiConversationService.addStoreMessage(
            { prompt: messageText, type: MessageType.SystemMsg },
            contextDataItems
          )
        )
      ),
    { dispatch: false }
  )

  #hasApplicableDataModelItems(draft: DataModelDraft | null): boolean {
    return (
      !!draft &&
      (draft.new.some((item) => !item.inactive) ||
        (draft.referenced ?? []).some((item) => !item.inactive))
    )
  }

  #resolveDataModelResourceId(): string {
    return (
      this.#store.selectSignal(selectCurrentResourceId)() ??
      this.#store.selectSignal(selectProcessResourceId)()
    )
  }
}
